import { gql, TypedDocumentNode } from '@apollo/client'
import { QueryPageableRequest, QueryPageableResponse } from '../../../core/type'
import {
  EligibleShippingMethod,
  ShippingCountry,
  ShippingEvaluationInput,
  ShippingMethod,
  ShippingMethodInput,
  ShippingMethodRuleInput,
  ShippingState,
} from '../type/shipping-method'

const METHOD_FIELDS = `
    id
    key
    title
    description
    checkoutNoticeTitle
    checkoutNotice
    kind
    basePrice
    sortOrder
    isActive
    isSystem
    deletedAt
    rules {
        id
        shippingMethodId
        label
        effect
        countryCode
        state
        minCartValue
        maxCartValue
        priceOverride
        sortOrder
        isActive
    }
`

export const GetShippingMethodsQuery: TypedDocumentNode<
  QueryPageableResponse<ShippingMethod, 'shippingMethodsFind'>,
  QueryPageableRequest<{ includeDeleted?: boolean }>
> = gql`
    query GetShippingMethods($request: ShippingMethodFindRequestInput!) {
        shippingMethodsFind(request: $request) {
            data {
                ${METHOD_FIELDS}
            }
            count
        }
    }
`

export const GetShippingCountriesQuery: TypedDocumentNode<{
  shippingCountries: ShippingCountry[]
}> = gql`
    query GetShippingCountries {
        shippingCountries {
            code
            name
        }
    }
`

export const GetStatesForCountryQuery: TypedDocumentNode<
  { states: ShippingState[] },
  { country: string }
> = gql`
    query GetShippingStates($country: String!) {
        states(country: $country) {
            id
            key
            name
        }
    }
`

export const EvaluateShippingMethodsQuery: TypedDocumentNode<
  { shippingMethodsEvaluate: EligibleShippingMethod[] },
  { input: ShippingEvaluationInput }
> = gql`
    query EvaluateShippingMethods($input: ShippingEvaluationInput!) {
        shippingMethodsEvaluate(input: $input) {
            id
            key
            title
            checkoutNoticeTitle
            checkoutNotice
            kind
            price
            sortOrder
            matchedRuleId
            matchedRuleLabel
            fallbackApplied
            fallbackReason
        }
    }
`

export const CreateShippingMethodMutation: TypedDocumentNode<
  void,
  { dto: ShippingMethodInput }
> = gql`
    mutation CreateShippingMethod($dto: ShippingMethodInput!) {
        shippingMethodCreate(dto: $dto)
    }
`

export const UpdateShippingMethodMutation: TypedDocumentNode<
  void,
  { id: number; dto: ShippingMethodInput }
> = gql`
    mutation UpdateShippingMethod($id: Int!, $dto: ShippingMethodInput!) {
        shippingMethodUpdate(id: $id, dto: $dto)
    }
`

export const ToggleActiveShippingMethodMutation: TypedDocumentNode<void, { id: number }> = gql`
    mutation ToggleActiveShippingMethod($id: Int!) {
        shippingMethodToggleActive(id: $id)
    }
`

export const ReorderShippingMethodsMutation: TypedDocumentNode<
  void,
  { dto: { ids: number[] } }
> = gql`
    mutation ReorderShippingMethods($dto: ShippingMethodReorderInput!) {
        shippingMethodReorder(dto: $dto)
    }
`

export const DeleteShippingMethodMutation: TypedDocumentNode<void, { id: number }> = gql`
    mutation DeleteShippingMethod($id: Int!) {
        shippingMethodDelete(id: $id)
    }
`

export const RestoreShippingMethodMutation: TypedDocumentNode<void, { id: number }> = gql`
    mutation RestoreShippingMethod($id: Int!) {
        shippingMethodRestore(id: $id)
    }
`

export const SaveShippingMethodRulesMutation: TypedDocumentNode<
  void,
  { shippingMethodId: number; dto: { rules: ShippingMethodRuleInput[] } }
> = gql`
    mutation SaveShippingMethodRules($shippingMethodId: Int!, $dto: ShippingMethodRulesSaveInput!) {
        shippingMethodRulesSave(shippingMethodId: $shippingMethodId, dto: $dto)
    }
`
